import React from 'react';
import { 
  Box, 
  Typography, 
  Container, 
  Card, 
  CardContent, 
  CardMedia,
  Avatar,
  Chip,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper
} from '@mui/material';
import { 
  CheckCircle, 
  School, 
  Work, 
  Star,
  Schedule,
  Phone
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';

const Team: React.FC = () => {
  const teamMembers = [
    {
      name: 'Dr. Javier Fernández',
      specialty: 'Odontopediatra',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300&h=300&fit=crop&crop=face',
      education: 'Universidad de Mendoza - Especialización en Odontopediatría',
      experience: '6+ años de experiencia',
      certifications: ['Odontología para Niños', 'Sedación Consciente', 'Prevención Dental Infantil'],
      description: 'Especialista en odontología infantil con enfoque en crear experiencias positivas para los niños.',
      specialties: ['Odontología preventiva infantil', 'Tratamiento de caries', 'Ortodoncia interceptiva', 'Traumatología dental'],
      availability: 'Lunes a Viernes 14:00-20:00'
    },
    {
      name: 'Dra. Patricia Ruiz',
      specialty: 'Estética Dental',
      image: 'https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=300&h=300&fit=crop&crop=face',
      education: 'Universidad de San Juan - Especialización en Estética Dental',
      experience: '7+ años de experiencia',
      certifications: ['Diseño de Sonrisa Digital', 'Carillas de Porcelana', 'Blanqueamiento Profesional'],
      description: 'Especialista en estética dental y diseño de sonrisas. Utiliza tecnología digital para planificar tratamientos estéticos.',
      specialties: ['Diseño de sonrisa', 'Carillas dentales', 'Blanqueamiento', 'Bonding estético'],
      availability: 'Lunes a Viernes 9:00-17:00'
    }
  ];

  const clinicFeatures = [
    'Equipo médico altamente calificado y certificado',
    'Tecnología de vanguardia en todos los tratamientos',
    'Atención personalizada y trato humano',
    'Formación continua y actualización constante',
    'Trabajo en equipo multidisciplinario',
    'Compromiso con la excelencia y calidad'
  ];

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          background: '#946f56',
          color: 'white',
          py: 6,
          textAlign: 'center'
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
            NUESTRO EQUIPO
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.9 }}>
            Profesionales altamente calificados comprometidos con tu salud bucal
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 6 }}>
        {/* Team Members */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'center' }}>
          {teamMembers.map((member, index) => (
            <Card 
              key={index} 
              sx={{ 
                maxWidth: 400,
                flex: '1 1 350px',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4
                }
              }}
            >
              <CardMedia
                component="img"
                height="250"
                image={member.image}
                alt={member.name}
              />
              <CardContent>
                <Typography variant="h5" component="h3" gutterBottom color="primary">
                  {member.name}
                </Typography>
                <Chip 
                  label={member.specialty} 
                  color="secondary" 
                  sx={{ mb: 2 }}
                />
                
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {member.description}
                </Typography>

                                 <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                   <School sx={{ mr: 1, color: 'primary.main' }} />
                   <Typography variant="body2">
                     {member.education}
                   </Typography>
                 </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Work sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="body2">
                    {member.experience}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Schedule sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="body2">
                    {member.availability}
                  </Typography>
                </Box>

                <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                  Especialidades:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                  {member.specialties.map((specialty, specIndex) => (
                    <Chip 
                      key={specIndex} 
                      label={specialty} 
                      size="small" 
                      variant="outlined"
                    />
                  ))}
                </Box>

                <Button
                  component={RouterLink}
                  to="/citas"
                  variant="contained"
                  fullWidth
                  startIcon={<Phone />}
                >
                  Agendar Cita
                </Button>
              </CardContent>
            </Card>
          ))}
        </Box>

        {/* Why Choose Our Team */}
        <Box sx={{ mt: 8, backgroundColor: 'grey.50', p: 4, borderRadius: 2 }}>
          <Typography variant="h4" component="h2" textAlign="center" gutterBottom>
            ¿Por qué elegir nuestro equipo?
          </Typography>
          <Typography variant="body1" textAlign="center" color="text.secondary" sx={{ mb: 4 }}>
            Nuestros profesionales se distinguen por su excelencia, experiencia y compromiso con la salud bucal
          </Typography>
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, justifyContent: 'center' }}>
            {clinicFeatures.map((feature, index) => (
              <Paper 
                key={index} 
                elevation={1} 
                sx={{ 
                  p: 2, 
                  flex: '1 1 250px',
                  textAlign: 'center',
                  backgroundColor: 'white'
                }}
              >
                <CheckCircle color="primary" sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="body1">
                  {feature}
                </Typography>
              </Paper>
            ))}
          </Box>
        </Box>

        {/* CTA Section */}
        <Box sx={{ mt: 6, textAlign: 'center' }}>
          <Typography variant="h5" component="h3" gutterBottom>
            ¿Listo para conocer a nuestro equipo?
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Agenda tu consulta y descubre por qué somos la mejor opción para tu salud bucal
          </Typography>
          <Button
            component={RouterLink}
            to="/citas"
            variant="contained"
            size="large"
            startIcon={<Schedule />}
            sx={{ fontSize: '1.1rem', px: 4, py: 1.5 }}
          >
            Agendar Consulta
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default Team; 